import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, Copy, ExternalLink, Megaphone } from 'lucide-react'
import Modal from '@/components/ui/Modal'
import { modalButton } from '@/components/ui/modalButton'
import Select from '@/components/ui/Select'
import { useCreateForm, useForms } from '@/features/forms/useForms'
import { useCreateList, useLists, useSendToList } from '@/features/lists/useLists'
import { useTemplates } from '@/features/messaging/useMessaging'
import { useMembers } from '@/features/team/useTeam'
import { useDeal } from '@/features/board/useBoard'
import { copyText, inputOf, retarget } from '@/features/forms/formBuilder'
import { plainTextToHtml } from '@/lib/messageFormat'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'
import { cn } from '@/lib/cn'
import type { Deal } from '@/api/types'
import type { Form, FormInput } from '@/api/forms'

const inputCls =
  'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40'

/** The enquiry form used when not starting from an existing one: name, mobile, email. */
const SIMPLE_FIELDS: FormInput['fields'] = [
  { key: 'first_name', label: 'First name', type: 'text', required: true, entity: 'contact', custom: false },
  { key: 'last_name', label: 'Last name', type: 'text', required: false, entity: 'contact', custom: false },
  { key: 'mobile', label: 'Mobile', type: 'tel', required: true, entity: 'contact', custom: false },
  { key: 'email', label: 'Email', type: 'email', required: true, entity: 'contact', custom: false },
]

const STEPS = ['Enquiry form', 'When someone enquires', 'Announce it'] as const

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
    </div>
  )
}

/**
 * An off-market campaign for one listing, in one go — what Calibre builds by hand in HubSpot for every
 * property (a form, a segment and a workflow, each named after the address):
 *   1. the enquiry form — a copy of last time's with the address swapped, or a simple one;
 *   2. what happens to each enquirer — their own list, an instant reply, the agent told;
 *   3. optionally, the announcement emailed to a list of buyers, with the form link.
 * Everything is linked to the deal, which then shows the campaign and its enquiries.
 */
export default function OffMarketWizard({ deal, onClose }: { deal: Deal; onClose: () => void }) {
  const { data: forms } = useForms()
  const { data: members } = useMembers()
  const { data: lists } = useLists()
  const { data: templates } = useTemplates('email')
  const createForm = useCreateForm()
  const createList = useCreateList()
  const send = useSendToList()

  const [step, setStep] = useState(0)
  const [address, setAddress] = useState(deal.title)
  const [formName, setFormName] = useState(`OM – ${deal.title}`)
  const [nameTouched, setNameTouched] = useState(false)

  // Start from the most recent earlier off-market form, if there is one — that's what Rusty clones today.
  const omForms = useMemo(
    () => (forms ?? []).filter((f) => f.listingDealId || /\bOM\b|off[- ]?market/i.test(f.name)).reverse(),
    [forms],
  )
  const [sourceId, setSourceId] = useState<string | null>(null)
  const effectiveSourceId = sourceId ?? omForms[0]?.id ?? ''
  const source: Form | undefined = (forms ?? []).find((f) => f.id === effectiveSourceId)
  // The old address to swap out: the source form's own listing title when it has one.
  const { data: sourceDeal } = useDeal(source?.listingDealId ?? undefined)
  const [oldText, setOldText] = useState<string | null>(null)
  const effectiveOldText = oldText ?? sourceDeal?.title ?? ''

  const owner = (members ?? []).find((m) => m.id === deal.ownerUserId)
  const [ownerId, setOwnerId] = useState(deal.ownerUserId ?? '')
  const [makeList, setMakeList] = useState(true)
  const [replyId, setReplyId] = useState<string | null>(null)
  const effectiveReplyId = replyId ?? source?.autoReplyTemplateId ?? ''
  const [notifyOwner, setNotifyOwner] = useState(true)

  const [created, setCreated] = useState<{ form: Form; listId: string | null } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [announceListId, setAnnounceListId] = useState('')
  const [subject, setSubject] = useState(`Off-market: ${deal.title}`)
  const [message, setMessage] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [sentInfo, setSentInfo] = useState<string | null>(null)

  const formLink = created ? `${window.location.origin}/f/${created.form.id}` : ''
  const announceList = (lists ?? []).find((l) => l.id === announceListId)
  const defaultMessage = `Hi {{first_name}},\n\nBefore it goes to market, we'd like to offer you a first look at ${address}.\n\nRegister your interest here: ${formLink}\n\n{{agent_signature}}`
  const body = message || defaultMessage

  async function createCampaign() {
    setError(null)
    try {
      let listId: string | null = null
      if (makeList) {
        const list = await createList.mutateAsync({
          name: `${formName} – enquirers`,
          listType: 'manual',
          description: `Everyone who enquired about ${address} (off-market).`,
        })
        listId = list.id
      }
      const assignee = (members ?? []).find((m) => m.id === ownerId)
      const base: FormInput = source
        ? retarget(inputOf(source), effectiveOldText, address)
        : {
            name: formName,
            fields: SIMPLE_FIELDS,
            createDeal: false,
            dealPipelineId: null,
            description: `Register your interest in ${address} before it goes to market.`,
            thankYouMessage: `Thanks — we've got your details and will be in touch about ${address} shortly.`,
            submitLabel: 'Register my interest',
          }
      const notify = new Set(base.notifyEmails ?? [])
      if (notifyOwner && assignee?.email) notify.add(assignee.email.toLowerCase())
      const form = await createForm.mutateAsync({
        ...base,
        name: formName.trim(),
        enabled: true,
        ownerUserId: ownerId || null,
        notifyEmails: [...notify],
        addToListId: listId ?? base.addToListId ?? null,
        autoReplyTemplateId: effectiveReplyId || null,
        listingDealId: deal.id,
      })
      setCreated({ form, listId })
      setStep(2)
    } catch (err) {
      setError(apiErrorMessage(err))
    }
  }

  function sendAnnouncement() {
    if (!announceListId) return
    send.mutate(
      { listId: announceListId, input: { channel: 'email', subject, body: plainTextToHtml(body) } },
      {
        onSuccess: (r) => {
          setConfirming(false)
          setSentInfo(
            r.queued > 0
              ? `Sending to ${r.queued} people in the background.`
              : `Sent to ${r.sent}${r.skipped ? ` (${r.skipped} skipped — unsubscribed or no email)` : ''}.`,
          )
        },
        onError: (err) => setError(apiErrorMessage(err)),
      },
    )
  }

  const busy = createForm.isPending || createList.isPending

  return (
    <Modal
      title="Launch an off-market campaign"
      icon={<Megaphone size={17} className="text-brand-primary" />}
      size="lg"
      onClose={onClose}
      footer={
        step === 0 ? (
          <>
            <button type="button" onClick={onClose} className={modalButton.ghost}>Cancel</button>
            <button type="button" onClick={() => setStep(1)} disabled={!address.trim() || !formName.trim()} className={modalButton.primary}>Next</button>
          </>
        ) : step === 1 ? (
          <>
            <button type="button" onClick={() => setStep(0)} className={modalButton.ghost}>Back</button>
            <button type="button" onClick={createCampaign} disabled={busy} className={modalButton.primary}>{busy ? 'Creating…' : 'Create the form'}</button>
          </>
        ) : (
          <>
            {!sentInfo && announceListId && !confirming && (
              <button type="button" onClick={() => setConfirming(true)} disabled={!subject.trim()} className={modalButton.primary}>
                Send to {announceList?.memberCount ?? 0} people…
              </button>
            )}
            {confirming && (
              <>
                <button type="button" onClick={() => setConfirming(false)} className={modalButton.ghost}>Not yet</button>
                <button type="button" onClick={sendAnnouncement} disabled={send.isPending} className={modalButton.primary}>
                  {send.isPending ? 'Sending…' : `Yes, send it now`}
                </button>
              </>
            )}
            {!confirming && <button type="button" onClick={onClose} className={sentInfo || !announceListId ? modalButton.primary : modalButton.ghost}>Done</button>}
          </>
        )
      }
    >
      <ol className="flex items-center gap-2 mb-5 text-xs">
        {STEPS.map((s, i) => (
          <li key={s} className="flex items-center gap-2">
            <span className={cn('w-5 h-5 rounded-full flex items-center justify-center font-medium', i < step ? 'bg-success text-white' : i === step ? 'bg-brand-primary text-white' : 'bg-gray-100 text-gray-400')}>
              {i < step ? <Check size={11} /> : i + 1}
            </span>
            <span className={i === step ? 'text-brand-darkest font-medium' : 'text-gray-400'}>{s}</span>
            {i < STEPS.length - 1 && <span className="w-6 h-px bg-gray-200" />}
          </li>
        ))}
      </ol>

      {step === 0 && (
        <div className="space-y-4">
          <Field label="Property">
            <input
              className={inputCls}
              value={address}
              onChange={(e) => {
                setAddress(e.target.value)
                if (!nameTouched) setFormName(`OM – ${e.target.value}`)
              }}
            />
          </Field>
          <Field label="Start from" hint="A copy of an earlier off-market form keeps its questions, look and thank-you page — with the address swapped.">
            <Select size="md" value={effectiveSourceId} onChange={(e) => { setSourceId(e.target.value); setOldText(null) }} className="w-full">
              <option value="">A simple enquiry form (name, mobile, email)</option>
              {omForms.map((f) => <option key={f.id} value={f.id}>Copy of “{f.name}”</option>)}
              {(forms ?? []).filter((f) => !omForms.includes(f)).length > 0 && (
                <optgroup label="Other forms">
                  {(forms ?? []).filter((f) => !omForms.includes(f)).map((f) => <option key={f.id} value={f.id}>Copy of “{f.name}”</option>)}
                </optgroup>
              )}
            </Select>
          </Field>
          {source && (
            <Field label="Address in that form, to replace" hint="Every mention — title, questions, thank-you text — becomes the new property.">
              <input className={inputCls} value={effectiveOldText} onChange={(e) => setOldText(e.target.value)} placeholder="e.g. 39 Grand Parade, Ashgrove" />
            </Field>
          )}
          <Field label="Form name">
            <input className={inputCls} value={formName} onChange={(e) => { setFormName(e.target.value); setNameTouched(true) }} />
          </Field>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-4">
          <Field label="Enquiries go to" hint="Each enquirer becomes a contact owned by this agent.">
            <Select size="md" value={ownerId} onChange={(e) => setOwnerId(e.target.value)} className="w-full">
              <option value="">Unassigned</option>
              {(members ?? []).map((m) => <option key={m.id} value={m.id}>{m.fullName}</option>)}
            </Select>
          </Field>
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input type="checkbox" className="mt-0.5" checked={notifyOwner} onChange={(e) => setNotifyOwner(e.target.checked)} />
            <span>Email {(members ?? []).find((m) => m.id === ownerId)?.fullName ?? owner?.fullName ?? 'the agent'} about each enquiry</span>
          </label>
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input type="checkbox" className="mt-0.5" checked={makeList} onChange={(e) => setMakeList(e.target.checked)} />
            <span>Put every enquirer on a new list, “{formName} – enquirers”</span>
          </label>
          <Field label="Email each enquirer straight away (optional)" hint="e.g. the property details they asked for. Sent from the agent, with their signature.">
            <Select size="md" value={effectiveReplyId} onChange={(e) => setReplyId(e.target.value)} className="w-full">
              <option value="">Don’t send an email</option>
              {(templates ?? []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </Select>
          </Field>
          {error && <p className="text-danger text-xs">{error}</p>}
        </div>
      )}

      {step === 2 && created && (
        <div className="space-y-4">
          <div className="rounded-lg border border-success/30 bg-success/5 p-3 text-sm">
            <p className="font-medium text-brand-darkest flex items-center gap-1.5"><Check size={15} className="text-success" /> “{created.form.name}” is live</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <code className="text-xs bg-white border border-gray-200 rounded px-2 py-1 truncate max-w-full">{formLink}</code>
              <button type="button" onClick={() => copyText(formLink)} className="inline-flex items-center gap-1 text-xs font-medium text-brand-primary hover:underline"><Copy size={12} /> Copy link</button>
              <Link to={`/forms/${created.form.id}`} className="inline-flex items-center gap-1 text-xs font-medium text-brand-primary hover:underline">Open the form <ExternalLink size={11} /></Link>
              {created.listId && <Link to={`/lists/${created.listId}`} className="inline-flex items-center gap-1 text-xs font-medium text-brand-primary hover:underline">Enquirers list <ExternalLink size={11} /></Link>}
            </div>
          </div>

          {sentInfo ? (
            <p className="text-sm text-success flex items-center gap-1.5"><Check size={15} /> {sentInfo}</p>
          ) : (
            <>
              <Field label="Announce it to (optional)" hint="Your buyers list, say. Skip this to share the link yourself.">
                <Select size="md" value={announceListId} onChange={(e) => setAnnounceListId(e.target.value)} className="w-full">
                  <option value="">Don’t send anything now</option>
                  {(lists ?? []).filter((l) => l.id !== created.listId).map((l) => (
                    <option key={l.id} value={l.id}>{l.name} · {l.memberCount} {l.memberCount === 1 ? 'person' : 'people'}</option>
                  ))}
                </Select>
              </Field>
              {announceListId && (
                <>
                  <Field label="Subject">
                    <input className={inputCls} value={subject} onChange={(e) => setSubject(e.target.value)} />
                  </Field>
                  <Field label="Message" hint="{{first_name}} becomes each person’s name; the agent’s signature is added at {{agent_signature}}. Unsubscribed people are skipped.">
                    <textarea className={inputCls} rows={7} value={body} onChange={(e) => setMessage(e.target.value)} />
                  </Field>
                  {confirming && (
                    <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      Send “{subject}” to {announceList?.memberCount ?? 0} people on {announceList?.name} now? This can’t be undone.
                    </p>
                  )}
                </>
              )}
              {error && <p className="text-danger text-xs">{error}</p>}
            </>
          )}
        </div>
      )}
    </Modal>
  )
}
